import React, { useState, useEffect } from 'react';
import ClientList from './components/ClientList';
import ClientProfile from './components/ClientProfile';
import MeetingForm from './components/MeetingForm';
import NewClientForm from './components/NewClientForm';
import SearchBar from './components/SearchBar';
import FilterBar from './components/FilterBar';
import LoginForm from './components/LoginForm';
import AdminPanel from './components/AdminPanel';
import ClientSection from './components/ClientSection';
import DashboardSection from './components/DashboardSection';
import AllTasksSection from './components/AllTasksSection';
import TaskView from './components/TaskView';
import { 
  loadClients, 
  createClient, 
  updateClient as updateClientApi, 
  deleteClient as deleteClientApi, 
  loginUser,
  createMeeting,
  updateMeeting
} from './utils/storage';

const App = () => {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showMeetingForm, setShowMeetingForm] = useState(false);
  const [editingMeetingData, setEditingMeetingData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    specialty: '',
    cell: '',
    month: '',
    dateFilter: '',
    dayOfWeek: '',
    weekRange: '',
    dateRange: { start: '', end: '' },
    sortBy: 'name',
    sortOrder: 'asc'
  });
  const [currentUser, setCurrentUser] = useState(null);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [currentPage, setCurrentPage] = useState('clients');

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Cargar usuario guardado en localStorage
        const savedUser = localStorage.getItem('currentUser');
        if (savedUser) {
          try {
            const parsedUser = JSON.parse(savedUser);
            setCurrentUser(parsedUser);
          } catch (parseError) {
            console.error('Error al parsear usuario guardado:', parseError);
            localStorage.removeItem('currentUser');
          }
        }

        const savedClients = await loadClients();
        setClients(savedClients);
        filterAndSortClients(savedClients, searchTerm, filters);
      } catch (error) {
        console.error('Error al cargar clientes:', error);
        setClients([]);
      }
    };
    loadInitialData();
  }, []);

  useEffect(() => {
    filterAndSortClients(clients, searchTerm, filters);
  }, [searchTerm, filters, clients]);

  const handleLogin = async (email, password) => {
    try {
      const response = await loginUser(email, password);
      setCurrentUser(response.user);
      
      // Guardar usuario en localStorage para persistir la sesión
      localStorage.setItem('currentUser', JSON.stringify(response.user));
      
      return response.user;
    } catch (error) {
      console.error('Error en login:', error);
      throw error;
    }
  };

  const handleLogout = () => {
    setCurrentUser(null);
    setSelectedClient(null);
    setShowMeetingForm(false);
    setEditingMeetingData(null);
    setShowAdminPanel(false);
    setCurrentPage('clients');
    
    // Limpiar usuario del localStorage
    localStorage.removeItem('currentUser');
  };

  const filterAndSortClients = (clientsToFilter, term, filterSettings) => {
    let filtered = [...clientsToFilter];

    if (term) {
      filtered = filtered.filter(client =>
        client.name.toLowerCase().includes(term.toLowerCase()) ||
        (client.specialty && client.specialty.toLowerCase().includes(term.toLowerCase()))
      );
    }

    if (filterSettings.specialty) {
      filtered = filtered.filter(client => 
        client.specialty === filterSettings.specialty
      );
    }

    if (filterSettings.cell) {
      if (currentUser && currentUser.role === 'standard' && currentUser.cells && currentUser.cells.length > 0) {
        filtered = filtered.filter(client => currentUser.cells.includes(client.cell));
      }
      if (filterSettings.cell) {
        filtered = filtered.filter(client => 
          client.cell && client.cell.toLowerCase().includes(filterSettings.cell.toLowerCase())
        );
      }
    } else {
      if (currentUser && currentUser.role === 'standard' && currentUser.cells && currentUser.cells.length > 0) {
        filtered = filtered.filter(client => currentUser.cells.includes(client.cell));
      }
    }

    if (filterSettings.month) {
      filtered = filtered.filter(client => 
        client.meetings.some(meeting => {
          const meetingDate = new Date(meeting.date);
          return (meetingDate.getMonth() + 1).toString() === filterSettings.month;
        })
      );
    }

    // Nuevos filtros de fecha (buscar en fechas de juntas Y fecha de creación del cliente) - Zona horaria México
    if (filterSettings.dateFilter) {
      filtered = filtered.filter(client => {
        // Extraer año, mes y día del filtro seleccionado
        const [filterYear, filterMonth, filterDay] = filterSettings.dateFilter.split('-').map(Number);
        
        // Función para comparar solo la fecha (sin hora) en zona horaria de México
        const isSameDateMexico = (dateString) => {
          const date = new Date(dateString);
          // Convertir a zona horaria de México
          const mexicoDate = new Date(date.toLocaleString("en-US", {timeZone: "America/Mexico_City"}));
          
          return mexicoDate.getFullYear() === filterYear &&
                 (mexicoDate.getMonth() + 1) === filterMonth &&
                 mexicoDate.getDate() === filterDay;
        };
        
        // Verificar fecha de creación del cliente
        if (client.createdAt) {
          if (isSameDateMexico(client.createdAt)) {
            return true;
          }
        }
        
        // Verificar fechas de juntas
        return client.meetings && client.meetings.some(meeting => {
          return isSameDateMexico(meeting.date);
        });
      });
    }

    if (filterSettings.dayOfWeek !== '') {
      filtered = filtered.filter(client => {
        // Función para obtener día de la semana en zona horaria de México
        const getMexicoDayOfWeek = (date) => {
          const mexicoDate = new Date(date.toLocaleString("en-US", {timeZone: "America/Mexico_City"}));
          return mexicoDate.getDay().toString();
        };
        
        // Verificar fecha de creación del cliente
        if (client.createdAt) {
          const clientCreatedDate = new Date(client.createdAt);
          if (getMexicoDayOfWeek(clientCreatedDate) === filterSettings.dayOfWeek) {
            return true;
          }
        }
        
        // Verificar fechas de juntas
        return client.meetings && client.meetings.some(meeting => {
          const meetingDate = new Date(meeting.date);
          return getMexicoDayOfWeek(meetingDate) === filterSettings.dayOfWeek;
        });
      });
    }

    if (filterSettings.dateRange.start && filterSettings.dateRange.end) {
      filtered = filtered.filter(client => {
        const startDate = new Date(filterSettings.dateRange.start);
        const endDate = new Date(filterSettings.dateRange.end);
        
        // Función para convertir fecha a zona horaria de México
        const getMexicoDate = (date) => {
          return new Date(date.toLocaleString("en-US", {timeZone: "America/Mexico_City"}));
        };
        
        // Verificar fecha de creación del cliente
        if (client.createdAt) {
          const clientCreatedDate = getMexicoDate(new Date(client.createdAt));
          if (clientCreatedDate >= startDate && clientCreatedDate <= endDate) {
            return true;
          }
        }
        
        // Verificar fechas de juntas
        return client.meetings && client.meetings.some(meeting => {
          const meetingDate = getMexicoDate(new Date(meeting.date));
          return meetingDate >= startDate && meetingDate <= endDate;
        });
      });
    }

    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch(filterSettings.sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'specialty':
          comparison = (a.specialty || '').localeCompare(b.specialty || '');
          break;
        case 'conversion':
          const aTotalContacts = a.meetings.reduce((total, m) => total + m.contactsGenerated, 0);
          const aTotalPatients = a.meetings.reduce((total, m) => total + m.patients, 0);
          const aRate = aTotalContacts > 0 ? aTotalPatients / aTotalContacts : 0;
          
          const bTotalContacts = b.meetings.reduce((total, m) => total + m.contactsGenerated, 0);
          const bTotalPatients = b.meetings.reduce((total, m) => total + m.patients, 0);
          const bRate = bTotalContacts > 0 ? bTotalPatients / bTotalContacts : 0;
          
          comparison = aRate - bRate;
          break;
        case 'date':
          const aLastMeeting = a.meetings.length > 0 ? Math.max(...a.meetings.map(m => new Date(m.date).getTime())) : 0;
          const bLastMeeting = b.meetings.length > 0 ? Math.max(...b.meetings.map(m => new Date(m.date).getTime())) : 0;
          comparison = aLastMeeting - bLastMeeting;
          break;
        default:
          comparison = 0;
      }
      
      return filterSettings.sortOrder === 'desc' ? -comparison : comparison;
    });

    setFilteredClients(filtered);
  };

  const handleSaveMeeting = async (meetingToSave) => {
    console.log("Guardando junta:", meetingToSave);
    console.log("¿Estamos editando?", editingMeetingData ? "SÍ" : "NO");
    console.log("ID de la junta:", meetingToSave.id);
    console.log("ID de la junta que se está editando:", editingMeetingData?.id);
    
    try {
      let updatedMeeting;
      
      if (editingMeetingData) {
        console.log("Actualizando junta existente");
        // Actualizar la junta existente usando updateMeeting
        await updateMeeting(editingMeetingData.id, meetingToSave);
        updatedMeeting = meetingToSave;
      } else {
        console.log("Creando nueva junta");
        // Crear nueva junta usando createMeeting
        const result = await createMeeting({
          ...meetingToSave,
          clientId: selectedClient.id
        });
        updatedMeeting = {
          ...meetingToSave,
          id: result.id
        };
      }
      
      // Actualizar el estado local
      const updatedClient = {
        ...selectedClient,
        meetings: editingMeetingData
          ? selectedClient.meetings.map(meeting =>
              meeting.id === editingMeetingData.id ? updatedMeeting : meeting
            )
          : [...selectedClient.meetings, updatedMeeting]
      };
      
      setClients(prevClients => 
        prevClients.map(client =>
          client.id === selectedClient.id ? updatedClient : client
        )
      );
      
      filterAndSortClients(clients.map(client =>
        client.id === selectedClient.id ? updatedClient : client
      ), searchTerm, filters);
      
      setShowMeetingForm(false);
      setEditingMeetingData(null);
      setSelectedClient(updatedClient);
    } catch (error) {
      console.error('Error al guardar la junta:', error);
      alert('Error al guardar la junta. Por favor, intente de nuevo.');
    }
  };

  const handleSaveNewClient = async (newClient) => {
    try {
      const result = await createClient(newClient);
      const newClientWithId = {
        id: result.id,
        ...newClient,
        meetings: []
      };
      
      const updatedClients = [...clients, newClientWithId];
      setClients(updatedClients);
      filterAndSortClients(updatedClients, searchTerm, filters);
      setCurrentPage('clients');
    } catch (error) {
      console.error('Error al crear cliente:', error);
      alert('Error al crear cliente. Por favor, intente de nuevo.');
    }
  };

  const handleBulkUploadClients = async (newClients) => {
    try {
      console.log('Iniciando carga masiva de clientes:', newClients);
      
      // Validar que tenemos clientes para cargar
      if (!newClients || newClients.length === 0) {
        throw new Error('No hay clientes para cargar');
      }

      // Asignar el salesExecutive actual a todos los clientes
      const clientsWithExecutive = newClients.map((client, index) => ({
        ...client,
        salesExecutive: currentUser.email,
        createdAt: new Date().toISOString(),
        meetings: []
      }));

      console.log('Clientes preparados para envío:', clientsWithExecutive);

      // Crear clientes uno por uno para mejor manejo de errores
      const createdClients = [];
      const errors = [];

      for (let i = 0; i < clientsWithExecutive.length; i++) {
        try {
          const client = clientsWithExecutive[i];
          console.log(`Creando cliente ${i + 1}/${clientsWithExecutive.length}:`, client.name);
          const result = await createClient(client);
          createdClients.push({
            id: result.id,
            ...client
          });
          console.log(`Cliente ${client.name} creado exitosamente con ID:`, result.id);
        } catch (clientError) {
          console.error(`Error al crear cliente ${clientsWithExecutive[i].name}:`, clientError);
          errors.push(`${clientsWithExecutive[i].name}: ${clientError.message}`);
        }
      }

      // Actualizar el estado con los clientes creados exitosamente
      if (createdClients.length > 0) {
        const updatedClients = [...clients, ...createdClients];
        setClients(updatedClients);
        filterAndSortClients(updatedClients, searchTerm, filters);
      }

      // Mostrar resultado
      if (errors.length === 0) {
        alert(`¡Éxito! Se cargaron exitosamente ${createdClients.length} clientes.`);
      } else if (createdClients.length > 0) {
        alert(`Se cargaron ${createdClients.length} clientes exitosamente.\n\nErrores en ${errors.length} clientes:\n${errors.join('\n')}`);
      } else {
        throw new Error(`No se pudo cargar ningún cliente:\n${errors.join('\n')}`);
      }

      setCurrentPage('clients');
    } catch (error) {
      console.error('Error general en carga masiva:', error);
      alert(`Error al cargar clientes: ${error.message}`);
    }
  };

  const handleUpdateClient = async (updatedClient) => {
    try {
      await updateClientApi(updatedClient.id, updatedClient);
      const updatedClients = clients.map(client => 
        client.id === updatedClient.id ? updatedClient : client
      );
      setClients(updatedClients);
      filterAndSortClients(updatedClients, searchTerm, filters);
      setSelectedClient(updatedClient);
    } catch (error) {
      console.error('Error al actualizar cliente:', error);
      alert('Error al actualizar cliente. Por favor, intente de nuevo.');
    }
  };

  const handleDeleteClient = async (clientId) => {
    try {
      await deleteClientApi(clientId);
      const updatedClients = clients.filter(client => client.id !== clientId);
      setClients(updatedClients);
      filterAndSortClients(updatedClients, searchTerm, filters);
    } catch (error) {
      console.error('Error al eliminar cliente:', error);
      alert('Error al eliminar cliente. Por favor, intente de nuevo.');
    }
  };

  const handleEditLastMeeting = (meeting) => {
    setEditingMeetingData(meeting);
    setShowMeetingForm(true);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleFilter = (newFilters) => {
    setFilters(newFilters);
  };

  // Verificar si estamos en la vista de tarea
  const searchParams = new URLSearchParams(window.location.search);
  if (searchParams.has('taskId')) {
    return <TaskView />;
  }

  if (!currentUser) {
    return <LoginForm onLogin={handleLogin} />;
  }

  const canEditAll = currentUser.role === 'admin';
  const canCreateClients = currentUser.role === 'admin'; // Solo admin puede crear clientes
  const canEditClients = currentUser.role === 'admin'; // Solo admin puede editar clientes
  const canCreateMeetings = currentUser.role === 'admin' || currentUser.role === 'standard'; // Ambos pueden crear juntas

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-4 p-4 rounded-lg bg-[#0061A8] text-[#FFFFFF]">
          <div className="flex items-center">
            <img 
              src="https://4tsix0yujj.ufs.sh/f/2vMRHqOYUHc0ruupdRNQiYtyo2GFJjxB80HOpNwcWZhvnAD4" 
              alt="Logo" 
              className="h-10 mr-3"
            />
            <h1 className="text-2xl font-bold">Expedientes de Clientes</h1>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm">Hola, {currentUser.firstName} {currentUser.lastName} ({currentUser.role})</span>
            {canEditAll && (
              <button
                onClick={() => setCurrentPage('admin')}
                className={`py-1 px-3 rounded-md text-sm transition-colors ${currentPage === 'admin' ? 'bg-[#A8D08D] text-[#0061A8]' : 'bg-[#63666A] text-[#FFFFFF] hover:bg-opacity-80'}`}
              >
                Administrar Usuarios
              </button>
            )}
            <button
              onClick={handleLogout}
              className="bg-red-500 text-[#FFFFFF] py-1 px-3 rounded-md text-sm hover:bg-red-600 transition-colors"
            >
              Cerrar Sesión
            </button>
          </div>
        </div>

        {/* Navegación principal */}
        <div className="flex space-x-4 mb-6">
          <button
            onClick={() => {
              setCurrentPage('clients');
              setSelectedClient(null);
              setShowMeetingForm(false);
              setEditingMeetingData(null);
            }}
            className={`py-2 px-4 rounded-md font-medium transition-colors ${currentPage === 'clients' ? 'bg-[#0061A8] text-[#FFFFFF]' : 'bg-white text-[#0061A8] hover:bg-[#D1D3D4]'}`}
          >
            Clientes
          </button>
          {canEditAll && (
            <button
              onClick={() => setCurrentPage('dashboard')}
              className={`py-2 px-4 rounded-md font-medium transition-colors ${currentPage === 'dashboard' ? 'bg-[#0061A8] text-[#FFFFFF]' : 'bg-white text-[#0061A8] hover:bg-[#D1D3D4]'}`}
            >
              Dashboard
            </button>
          )}
          <button
            onClick={() => setCurrentPage('allTasks')}
            className={`py-2 px-4 rounded-md font-medium transition-colors ${currentPage === 'allTasks' ? 'bg-[#0061A8] text-[#FFFFFF]' : 'bg-white text-[#0061A8] hover:bg-[#D1D3D4]'}`}
          >
            Todas las Tareas
          </button>
        </div>

        {currentPage === 'admin' ? (
          <AdminPanel onBack={() => setCurrentPage('clients')} currentUser={currentUser} />
        ) : currentPage === 'dashboard' ? (
          <DashboardSection clients={clients} />
        ) : currentPage === 'allTasks' ? (
          <AllTasksSection onClose={() => setCurrentPage('clients')} currentUser={currentUser} />
        ) : selectedClient ? (
          showMeetingForm ? (
            <MeetingForm 
              client={selectedClient}
              onSave={handleSaveMeeting}
              onCancel={() => {
                setShowMeetingForm(false);
                setEditingMeetingData(null);
              }}
              initialMeetingData={editingMeetingData}
            />
          ) : (
            <ClientProfile 
              client={selectedClient}
              onBack={() => setSelectedClient(null)}
              onNewMeeting={canCreateMeetings ? () => {
                setEditingMeetingData(null);
                setShowMeetingForm(true);
              } : null}
              onEditMeeting={handleEditLastMeeting}
              onUpdateClient={canEditClients ? handleUpdateClient : null}
              canEdit={canEditClients}
              currentUser={currentUser}
            />
          )
        ) : (
          <ClientSection
            clients={clients}
            searchTerm={searchTerm}
            onSearch={handleSearch}
            filters={filters}
            onFilter={handleFilter}
            onSelectClient={setSelectedClient}
            onNewMeeting={canCreateMeetings ? (client) => {
                setSelectedClient(client);
                setEditingMeetingData(null);
                setShowMeetingForm(true);
              } : null}
            onSaveNewClient={canCreateClients ? (newClient) => handleSaveNewClient({ ...newClient, salesExecutive: currentUser.email }) : null}
            onBulkUploadClients={canCreateClients ? handleBulkUploadClients : null}
            canCreateClients={canCreateClients}
            canCreateMeetings={canCreateMeetings}
            onUpdateClient={canEditClients ? handleUpdateClient : null}
            onDeleteClient={canEditAll ? handleDeleteClient : null}
            currentUser={currentUser}
          />
        )}
      </div>
    </div>
  );
};

export default App;
